import React, { useMemo, useCallback } from 'react';
import { Input } from 'app/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { useFormContext } from 'react-hook-form';
import FormField from './components/FormField';
import useOptimizedFormWatch from './hooks/useOptimizedFormWatch';

function Details() {
  const form = useFormContext();
  const watchedValues = useOptimizedFormWatch();

  // Memoize handlers to prevent unnecessary re-renders
  const handleProductNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setValue('product_name', e.target.value);
      form.setValue('slug', e.target.value.toLowerCase().replace(/ /g, '-'));
    },
    [form],
  );

  const handleVariantModeChange = useCallback(
    (value: string) => {
      form.setValue('variant_mode', value);
    },
    [form],
  );

  // Memoize conditional sections to prevent unnecessary re-renders
  const productTypeSection = useMemo(() => {
    if (!watchedValues.productType) return null;

    return (
      <FormField
        label="Product Type"
        helpText={
          watchedValues.variantMode === 'single'
            ? 'One price and configuration for the entire product'
            : 'Multiple variants with different prices and options'
        }
        color="orange"
      >
        <Select
          value={watchedValues.variantMode}
          onValueChange={handleVariantModeChange}
        >
          <SelectTrigger className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 rounded-lg transition-all duration-200 text-sm">
            <SelectValue placeholder="Choose product structure" />
          </SelectTrigger>
          <SelectContent position="popper" side="bottom" align="start">
            <SelectItem value="single">Single Product</SelectItem>
            <SelectItem value="variant">Variant Product</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
    );
  }, [
    watchedValues.productType,
    watchedValues.variantMode,
    handleVariantModeChange,
  ]);

  const skuSection = useMemo(() => {
    if (watchedValues.variantMode === 'variant') return null;

    return (
      <FormField
        label="SKU (Stock Keeping Unit)"
        helpText="Optional unique identifier for inventory tracking"
        color="purple"
      >
        <Input
          id="sku"
          placeholder="Enter SKU"
          type="text"
          className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 rounded-lg transition-all duration-200 text-sm"
          {...form.register('sku')}
        />
      </FormField>
    );
  }, [watchedValues.variantMode, form]);

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Product Details
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Basic product information
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Product Name */}
        <FormField label="Product Name" helpText="" color="blue" required>
          <Input
            id="product-name"
            placeholder="Enter product name"
            type="text"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('product_name', {
              required: 'Product Name is required',
            })}
            onChange={handleProductNameChange}
          />
          {form?.formState?.errors?.product_name && (
            <p className="text-red-500 text-xs font-medium">
              {typeof form.formState?.errors?.product_name?.message ===
                'string' && form?.formState?.errors.product_name.message}
            </p>
          )}
        </FormField>

        {/* Slug */}
        <FormField
          label="URL Slug"
          helpText="This will be used in the product URL"
          color="green"
          required
        >
          <Input
            id="slug"
            placeholder="product-url-slug"
            type="text"
            className="h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-lg transition-all duration-200 text-sm"
            {...form.register('slug', {
              required: 'Slug is required',
            })}
          />
          {form?.formState?.errors?.slug && (
            <p className="text-red-500 text-xs font-medium">
              {typeof form?.formState?.errors?.slug?.message === 'string' &&
                form?.formState?.errors.slug.message}
            </p>
          )}
        </FormField>

        {/* Product Type */}
        {productTypeSection}

        {/* SKU - Only show for single products */}
        {skuSection}
      </div>
    </div>
  );
}

export default Details;
